import axios from '../../../api-client.ts';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { toAbsoluteStatic } from '../../../../_mesomb/helpers';
import i18n from '../../../../_mesomb/i18n';
import ButtonIndicator from '../../../../_mesomb/partials/components/ButtonIndicator.tsx';
import OtpInput from '../../../../_mesomb/partials/form/OtpInput.tsx';
import { AuthError } from '../../../../types';

const authenticatorCodeSchema = Yup.object().shape({
  code: Yup.string()
    .matches(/^\d{6}$/, i18n.t('Invalid code'))
    .required(i18n.t('Field_is_required')),
});
const recoveryCodeSchema = Yup.object().shape({
  code: Yup.string()
    .matches(/^\d{8}$/, i18n.t('Enter one of your 8-digit recovery codes'))
    .required(i18n.t('Field_is_required')),
});
type FormTwoFactorAuthValues = Yup.InferType<typeof authenticatorCodeSchema>;

function TwoFactorAuth({
  onSubmit,
  onClose,
}: {
  onSubmit: (code: string) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [usingRecoveryCode, setUsingRecoveryCode] = useState(false);
  const formik = useFormik<FormTwoFactorAuthValues>({
    initialValues: { code: '' },
    validationSchema: usingRecoveryCode ? recoveryCodeSchema : authenticatorCodeSchema,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        setSubmitting(true);
        await onSubmit(values.code);
        setSubmitting(false);
      } catch (error) {
        setSubmitting(false);
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
          const errors: Record<string, string> = (
            error.response.data.errors as AuthError[]
          ).reduce((acc, item) => ({ ...acc, [item.param]: item.message }), {});
          if (errors.key) {
            errors.code = errors.key;
          }
          setErrors(errors);
        }
      }
    },
  });

  return (
    <form
      className='form w-100'
      id='kt_sign_in_form'
      onSubmit={formik.handleSubmit}
      noValidate
    >
      <div className='text-center mb-10'>
        <img
          alt='Logo'
          className='mh-125px'
          src={toAbsoluteStatic('/static/images/svg/misc/smartphone-2.svg')}
        />
      </div>

      <div className='text-center mb-10'>
        <h1 className='text-dark mb-3'>{t('Two-Factor Verification')}</h1>
        <div className='text-muted fw-semibold fs-5 mb-5'>
          {usingRecoveryCode
            ? t('Enter an unused recovery code to sign in.')
            : t('Your account is protected by two-factor authentication.')}
        </div>
      </div>

      {formik.status ? (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      ) : null}

      <div className='fv-row mb-3'>
        <label className='form-label fs-6 fw-bolder text-gray-900'>
          {t(usingRecoveryCode ? 'Enter a recovery code' : 'Enter the code')}
        </label>
        <OtpInput
          key={usingRecoveryCode ? 'recovery-code' : 'authenticator-code'}
          name={'code'}
          onComplete={(otp) => formik.setFieldValue('code', otp)}
          isSecure={false}
          length={usingRecoveryCode ? 8 : 6}
          inputType={'numeric'}
        />
        {formik.touched.code && formik.errors.code && (
          <div className='fv-plugins-message-container form-text text-danger'>
            <span role='alert'>{formik.errors.code}</span>
          </div>
        )}
      </div>

      <div className='text-center mb-7'>
        <button
          type='button'
          className='btn btn-link btn-sm fw-semibold p-0'
          onClick={() => {
            setUsingRecoveryCode((value) => !value);
            formik.resetForm();
          }}
        >
          {t(usingRecoveryCode ? 'Use an authenticator code instead' : 'Use a recovery code instead')}
        </button>
        <div className='text-muted fs-7 mt-2'>
          {usingRecoveryCode
            ? t('Use the current 6-digit code from your authenticator app.')
            : t('Use this only if you cannot access your authenticator app. Each recovery code works once.')}
        </div>
      </div>

      <div className='d-grid mb-10'>
        <ButtonIndicator
          label={t('Confirm')}
          type={'submit'}
          loading={formik.isSubmitting}
          disabled={formik.isSubmitting || !formik.isValid}
        />
        <button
          type='button'
          id='kt_login_signup_form_cancel_button'
          className='btn btn-lg btn-light-primary w-100 mt-5'
          onClick={onClose}
        >
          {t('Cancel')}
        </button>
      </div>
    </form>
  );
}

export default TwoFactorAuth;
